import {
  ChannelBadge,
  type ChannelKind,
  DataTable,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ERROR_CATEGORY_INFO } from '../../constants/error-categories';
import type { Notification } from '../../schemas/notification.schema';
import { formatRelativeTime } from '../../utils/date';
import { isStuckInQueue } from '../../utils/notification-retry';
import { statusTone } from '../../utils/status-tone';

interface NotificationListProps {
  notifications: Notification[];
  loading: boolean;
  error: Error | null | undefined;
  page: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function NotificationList({
  notifications,
  loading,
  error,
  page,
  pageCount,
  pageSize,
  onPageChange,
}: NotificationListProps) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Notification>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <span title={row.original.id} className="font-mono text-xs text-muted-foreground">
            {row.original.id.substring(0, 8)}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className="inline-flex flex-wrap items-center gap-1">
            <StatusBadge tone={statusTone(row.original.status)}>{row.original.status}</StatusBadge>
            {/* QUEUED sin ningun intento por mas de 10 min: nunca llego al
                worker (RFC 0002 Fase 2). Sin esta marca "QUEUED" se lee como
                "en curso" cuando en realidad esta trabado. */}
            {isStuckInQueue(row.original) ? (
              <StatusBadge tone="warning" title="Queued with no delivery attempt for over 10 min">
                stuck
              </StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'channel',
        header: 'Channel',
        cell: ({ row }) => <ChannelBadge channel={row.original.channel as ChannelKind} />,
      },
      {
        accessorKey: 'eventType',
        header: 'Event type',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.eventType}</span>,
      },
      {
        accessorKey: 'to',
        header: 'Recipient',
        cell: ({ row }) => (
          <span className="block max-w-[220px] truncate text-sm">{row.original.to}</span>
        ),
      },
      {
        accessorKey: 'lastErrorCategory',
        header: 'Last error',
        cell: ({ row }) => {
          const { lastErrorCategory, lastErrorCode } = row.original;
          if (!lastErrorCategory && !lastErrorCode) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          const categoryInfo = lastErrorCategory ? ERROR_CATEGORY_INFO[lastErrorCategory] : null;
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              {categoryInfo ? (
                <StatusBadge tone={categoryInfo.tone} title={categoryInfo.description}>
                  {categoryInfo.label}
                </StatusBadge>
              ) : null}
              {lastErrorCode ? (
                <span className="font-mono text-xs text-muted-foreground">{lastErrorCode}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span
            title={new Date(row.original.createdAt).toLocaleString()}
            className="text-xs text-muted-foreground"
          >
            {formatRelativeTime(row.original.createdAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable
      columns={columns}
      data={notifications}
      isLoading={loading}
      error={error}
      onRowClick={(row) => navigate(`/notifications/${row.id}`)}
      pageCount={pageCount}
      pagination={{ pageIndex: page - 1, pageSize }}
      onPaginationChange={(state) => onPageChange(state.pageIndex + 1)}
    />
  );
}
